import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n";

const linkDefs = [
  { href: "#process", key: "process" },
  { href: "#work", key: "work" },
  { href: "#for-wholesalers", key: "wholesalers" },
  { href: "/about", key: "about", page: true },
  { href: "/contact", key: "contact", page: true },
];

const Header = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const hrefFor = (l) => (l.page ? l.href : pathname === "/" ? l.href : `/${l.href}`);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-bone/80 backdrop-blur-xl"
    >
      <div className="relative flex flex-col items-center gap-1.5 border-b border-ink/10 px-6 pb-3 pt-4">
        <a href="/" data-testid="header-logo" className="flex flex-col items-center gap-1.5">
          <img src="/images/logo.png" alt="HHB Group — fashion clothing" className="h-14 w-auto md:h-16" />
          <span className="font-serif text-xl font-semibold tracking-tight text-ink md:text-2xl">
            HHB <span className="italic text-clay">Clothing Co</span>
          </span>
        </a>
        <div className="absolute end-4 top-1/2 flex -translate-y-1/2 flex-col items-end gap-2 md:end-10">
          <button
            onClick={() => setLang(lang === "en" ? "ar" : "en")}
            data-testid="language-switch"
            aria-label="Switch language"
            className="rounded-full border border-ink/20 bg-bone px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-clay hover:text-clay"
          >
            {t.nav.switchTo}
          </button>
          <a
            href={pathname === "/" ? "#quote" : "/#quote"}
            data-testid="header-quote-button"
            className="hidden rounded-full bg-ink px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay md:inline-flex"
          >
            {t.nav.quote}
          </a>
        </div>
      </div>

      <div className="relative mx-auto flex h-14 max-w-7xl items-center justify-center px-6 md:px-12">
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {linkDefs.map((l) => (
            <a
              key={l.key}
              href={hrefFor(l)}
              data-testid={`nav-link-${l.key}`}
              className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/60 transition-colors duration-300 hover:text-clay"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>
        <div className="absolute end-6 flex items-center md:end-12 lg:hidden">
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/20 text-ink"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-ink/10 bg-bone lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {linkDefs.map((l) => (
                <a
                  key={l.key}
                  href={hrefFor(l)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="py-3 text-center font-serif text-2xl tracking-tight text-ink transition-colors duration-300 hover:text-clay"
                >
                  {t.nav[l.key]}
                </a>
              ))}
              <a
                href={pathname === "/" ? "#quote" : "/#quote"}
                onClick={() => setOpen(false)}
                data-testid="mobile-quote-button"
                className="mx-auto mt-4 inline-flex w-fit rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone"
              >
                {t.nav.quote}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
